import React from 'react';
import { FmBanner } from './FmBanner';
import { FmButton } from './FmButton';
import { FmChip } from './FmChip';
import { FmHeading, FmInline, FmStack, FmText } from './FmComposition';
import { FmDataValue } from './FmDataValue';
import { FmEmpty, FmSpinner } from './FmFeedback';
import { FmFieldGrid } from './FmField';
import { Field } from './FmFormFlowFields';
import { FmEvidence } from './FmEvidence';
import { FmTable } from './FmTable';
import { FmValueList } from './FmValueList';
import { FmFormFlowEditableCollection } from './FmFormFlowEditableCollection';
import type {
  FmFormFlowBlockDescriptor,
  FmFormFlowControlDescriptor,
  FmFormFlowEvent,
  FmFormFlowPageDescriptor,
  FmFormFlowTableRowDescriptor,
  FmFormFlowValueDescriptor,
} from './FmFormFlowPage.types';
import LanguageContext from '@/i18n/LanguageContext';
import {
  getMapSlot,
  isPrimaryMapSlotKey,
  type FmPrimaryMapSlotProps,
} from '@/shared/config/mapSlotRegistry';

const VALUE_TONE = {
  default: 'default',
  positive: 'positive',
  warning: 'accent',
  negative: 'negative',
} as const;
const FORM_FLOW_RECORD_ROLE = 'radio' as const;

function emit(descriptor: FmFormFlowPageDescriptor, event: FmFormFlowEvent): void {
  descriptor.onEvent?.(event);
}

export function Values({
  values,
}: {
  values: readonly FmFormFlowValueDescriptor[];
}): React.ReactElement {
  const language = React.useContext(LanguageContext)?.language ?? 'fi';
  const locale = {
    en: 'en-GB',
    fi: 'fi-FI',
    sv: 'sv-SE',
    es: 'es-ES',
  }[language];
  return (
    <FmValueList
      density="compact"
      items={values.map((item) => ({
        id: item.id,
        label: item.label,
        value: (
          <FmText tone={VALUE_TONE[item.status ?? 'default']}>
            <FmDataValue
              locale={locale}
              rule={
                typeof item.value === 'number'
                  ? {
                      kind: 'number',
                      value: item.value,
                      unit: item.unit,
                      /* ONE decimal for a measurement, none for a bare count. Two
                       decimals printed a parcel as "38,40 ha" beside a stand
                       shown as "4.8 ha" on the same page — a hectare precision
                       the rest of the product does not use. */
                      precision: item.unit ? 1 : 0,
                    }
                  : {
                      kind: 'text',
                      value: typeof item.value === 'boolean' ? String(item.value) : item.value,
                      unit: item.unit,
                    }
              }
            />
          </FmText>
        ),
        helperText: item.sourceLabel,
      }))}
    />
  );
}

function Fields({
  block,
  descriptor,
}: {
  block: FmFormFlowBlockDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  const control = block.control as Extract<FmFormFlowControlDescriptor, { type: 'fields' }>;
  return (
    <FmFieldGrid
      columns={2}
      className={control.fields.length === 1 ? 'fm-form-flow__single-field' : undefined}
    >
      {control.fields.map((field) => (
        <Field key={field.id} blockId={block.id} field={field} descriptor={descriptor} />
      ))}
    </FmFieldGrid>
  );
}

function RecordList({
  block,
  descriptor,
}: {
  block: FmFormFlowBlockDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  const control = block.control as Extract<
    FmFormFlowControlDescriptor,
    { type: 'collection'; mode: 'records' }
  >;
  if (!control.records.length) return <FmEmpty description={control.emptyLabel} />;
  return (
    <div
      className="fm-form-flow__records"
      role={control.selectionMode === 'single' ? 'radiogroup' : 'list'}
      aria-label={control.selectionMode === 'single' ? block.title : undefined}
    >
      {control.records.map((record) => {
        const content = (
          <FmStack gap="sm">
            <FmInline justify="between" block>
              <FmText weight="bold">{record.title}</FmText>
              {record.statusLabel ? (
                <FmChip variant={record.selected ? 'success' : 'mute'}>{record.statusLabel}</FmChip>
              ) : null}
            </FmInline>
            {record.description ? (
              <FmText size="fine" tone="muted">
                {record.description}
              </FmText>
            ) : null}
            <Values values={record.values} />
          </FmStack>
        );
        return control.selectionMode === 'single' ? (
          <button
            key={record.id}
            type="button"
            className="fm-form-flow__record"
            role={FORM_FLOW_RECORD_ROLE}
            aria-checked={Boolean(record.selected)}
            onClick={() =>
              emit(descriptor, { kind: 'record-select', blockId: block.id, recordId: record.id })
            }
          >
            {content}
          </button>
        ) : (
          <li key={record.id} className="fm-form-flow__record">
            {content}
          </li>
        );
      })}
    </div>
  );
}

function MapBlock({
  block,
  descriptor,
}: {
  block: FmFormFlowBlockDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  const control = block.control as Extract<FmFormFlowControlDescriptor, { type: 'map' }>;
  const Slot = getMapSlot(control.slotKey);
  if (control.mode === 'select') {
    const SelectionSlot = Slot as React.ComponentType<FmPrimaryMapSlotProps> | undefined;
    const slotProps = {
      propertyId: control.propertyId,
      sourceId: control.sourceId,
      layerIds: [...control.layerIds],
      selectedFeatureId: control.selectedFeatureId,
      selectedFeatureKind: control.selectedFeatureKind,
      measurementPlotRefs: control.measurementPlotRefs,
      interactionMode: 'select' as const,
      onFeatureSelect: (featureId: string) =>
        emit(descriptor, {
          kind: 'map-select',
          blockId: block.id,
          featureId,
        }),
    };
    const map = !SelectionSlot ? null : control.preview ? (
      <SelectionSlot {...slotProps} preview mapData={null} />
    ) : (
      <SelectionSlot {...slotProps} preview={false} mapData={control.mapData} />
    );
    return (
      <FmStack gap="sm">
        <FmText size="fine" tone="muted">
          {control.selectionLabel}
        </FmText>
        {map ? (
          <div className="fm-form-flow__map" data-testid={`form-flow-map-${block.id}`}>
            {map}
          </div>
        ) : (
          <FmEmpty description={control.emptyLabel} />
        )}
      </FmStack>
    );
  }
  const reviewSlotProps = {
    propertyId: control.propertyId,
    sourceId: control.sourceId,
    layerIds: [...control.layerIds],
    selectedFeatureId: control.selectedFeatureId,
    interactionMode: 'static' as const,
  };
  let reviewMap: React.ReactElement | null = null;
  if (Slot) {
    if (isPrimaryMapSlotKey(control.slotKey)) {
      const PrimaryReviewSlot = Slot as React.ComponentType<FmPrimaryMapSlotProps>;
      if (control.preview) {
        reviewMap = <PrimaryReviewSlot {...reviewSlotProps} preview mapData={null} />;
      } else {
        if (!control.mapData) {
          throw new Error('Primary review maps require resolved map data.');
        }
        reviewMap = (
          <PrimaryReviewSlot {...reviewSlotProps} preview={false} mapData={control.mapData} />
        );
      }
    } else {
      reviewMap = <Slot {...reviewSlotProps} />;
    }
  }
  return (
    <FmStack gap="sm">
      <FmInline gap="sm" wrap>
        <FmChip variant="mute" multiline>
          {control.sourceLabel}
        </FmChip>
        {control.observedAt ? (
          <FmText size="caption" tone="muted">
            {control.observedAt}
          </FmText>
        ) : null}
      </FmInline>
      {reviewMap ? (
        <div className="fm-form-flow__map" data-testid={`form-flow-map-${block.id}`}>
          {reviewMap}
        </div>
      ) : (
        <FmEmpty description={control.emptyLabel} />
      )}
    </FmStack>
  );
}

function ReviewTable({ block }: { block: FmFormFlowBlockDescriptor }): React.ReactElement {
  const control = block.control as Extract<
    FmFormFlowControlDescriptor,
    { type: 'collection'; mode: 'review' }
  >;
  return (
    <FmTable<FmFormFlowTableRowDescriptor>
      caption={control.caption}
      columns={control.columns.map((column) => ({
        key: column.id,
        header: column.label,
        label: column.label,
        role: column.role,
        render: (row) => {
          const value = row.cells[column.id];
          return (
            <FmDataValue
              rule={{ kind: 'text', value: typeof value === 'boolean' ? String(value) : value }}
            />
          );
        },
      }))}
      rows={[...control.rows]}
      rowKey={(row) => row.id}
      emptyText={control.emptyLabel}
      density="compact"
      mobileLayout="labelled-cells"
    />
  );
}

function OneTimeSecret({
  block,
  descriptor,
}: {
  block: FmFormFlowBlockDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  const control = block.control as Extract<FmFormFlowControlDescriptor, { type: 'secret' }>;
  if (control.status === 'consumed')
    return (
      <FmEmpty
        description={control.warning}
        data-testid={`form-flow-secret-${block.id}-consumed`}
      />
    );
  const copy = async (): Promise<void> => {
    if (!control.secret) return;
    await navigator.clipboard.writeText(control.secret);
    emit(descriptor, { kind: 'secret-copied', blockId: block.id });
  };
  return (
    <FmStack gap="sm" data-testid={`form-flow-secret-${block.id}`}>
      <FmBanner tone="warning" title={control.warning} />
      <FmText size="caption" tone="muted">
        {control.secretLabel}
      </FmText>
      <code className="fm-form-flow__secret">{control.secret}</code>
      <FmInline gap="sm" wrap>
        <FmButton variant="bordered" onClick={() => void copy()}>
          {control.status === 'copied' ? control.copiedLabel : control.copyLabel}
        </FmButton>
        <FmButton
          variant="primary"
          onClick={() => emit(descriptor, { kind: 'secret-consumed', blockId: block.id })}
        >
          {control.acknowledgeLabel}
        </FmButton>
      </FmInline>
    </FmStack>
  );
}

function BlockBody({
  block,
  descriptor,
}: {
  block: FmFormFlowBlockDescriptor;
  descriptor: FmFormFlowPageDescriptor;
}): React.ReactElement {
  const { control } = block;
  switch (control.type) {
    case 'fields':
      return <Fields block={block} descriptor={descriptor} />;
    case 'map':
      return <MapBlock block={block} descriptor={descriptor} />;
    case 'collection':
      if (control.mode === 'records') return <RecordList block={block} descriptor={descriptor} />;
      if (control.mode === 'evidence')
        return (
          <FmStack gap="md">
            <Values values={control.values} />
            <FmEvidence evidence={control.evidence} />
          </FmStack>
        );
      if (control.mode === 'review') return <ReviewTable block={block} />;
      return <FmFormFlowEditableCollection block={block} descriptor={descriptor} />;
    case 'secret':
      return <OneTimeSecret block={block} descriptor={descriptor} />;
    case 'notice':
      return (
        <FmBanner
          tone={control.tone}
          title={block.title}
          description={block.description}
          onClose={
            control.dismissible
              ? () => emit(descriptor, { kind: 'notice-dismissed', blockId: block.id })
              : undefined
          }
        />
      );
  }
}

function BlockState({ block }: { block: FmFormFlowBlockDescriptor }): React.ReactElement | null {
  if (!block.state || block.state === 'ready' || block.state === 'saved') return null;
  if (block.state === 'loading' || block.state === 'saving')
    return <FmSpinner tip={block.stateLabel} />;
  return (
    <FmEmpty description={block.stateLabel} role={block.state === 'error' ? 'alert' : 'status'} />
  );
}

export const FmFormFlowBlocks: React.FC<{
  blocks: readonly FmFormFlowBlockDescriptor[];
  descriptor: FmFormFlowPageDescriptor;
  dominantBeatId?: string;
  showHeaders?: boolean;
  layout?: 'stack' | 'spatial';
  /**
   * The stage's state notice, rendered inside `noticeBlockId` — under that
   * block's heading and above its control, because the outcome belongs to the
   * control that produced it.
   */
  notice?: React.ReactNode;
  noticeBlockId?: string;
  /**
   * A lock applies to CONTROLS. The disabled fieldset therefore wraps each
   * block's body and nothing else: a heading is not a control, and neither is
   * the notice that tells the owner how to lift the lock — wrapping the whole
   * region made the one button that resolves the state inert
   *
   * REQUIRED, deliberately. A default of `true` on the only mechanism that
   * disables controls behind a PAID boundary fails open: a future caller that
   * forgets the prop unlocks the boundary silently instead of failing to
   * compile.
   */
  controlsEnabled: boolean;
  busy?: boolean;
  previewBehindLock?: boolean;
}> = ({
  blocks,
  descriptor,
  dominantBeatId,
  showHeaders = true,
  layout = 'stack',
  notice,
  noticeBlockId,
  controlsEnabled,
  busy,
  previewBehindLock,
}) => (
  <div className="fm-form-flow__blocks" data-layout={layout} data-testid="fm-form-flow-blocks">
    {blocks.map((block) => (
      <section
        className="fm-form-flow__block"
        data-control-type={block.control.type}
        data-dominant={block.id === dominantBeatId || undefined}
        key={block.id}
        data-testid={`form-flow-block-${block.id}`}
      >
        {block.control.type === 'notice' || !showHeaders ? null : (
          <header className="fm-form-flow__block-header">
            <FmHeading level={3}>{block.title}</FmHeading>
            {block.description ? <FmText tone="muted">{block.description}</FmText> : null}
          </header>
        )}
        {block.id === noticeBlockId ? notice : null}
        <fieldset
          className="fm-form-flow__block-region"
          disabled={!controlsEnabled}
          aria-busy={busy || undefined}
          /* A previewed paid boundary is READABLE but not operable, and
             `fieldset disabled` is exactly that: its controls are announced and
             disabled rather than removed. `inert` was the wrong instrument — it
             drops the whole subtree out of the accessibility tree, so a
             screen-reader owner could not perceive what the package unlocks at
             all, and it is unsupported on older Safari, where the boundary would
             silently leak. Blocks that `disabled` cannot reach are refused by
             the validator instead. */
          data-preview-behind-lock={previewBehindLock ? '' : undefined}
        >
          <div className="fm-form-flow__block-body">
            <BlockState block={block} />
            {!block.state || block.state === 'ready' || block.state === 'saved' ? (
              <BlockBody block={block} descriptor={descriptor} />
            ) : null}
          </div>
        </fieldset>
      </section>
    ))}
  </div>
);

export function ActiveFlowHeading({
  title,
  id,
}: {
  title: string;
  id: string;
}): React.ReactElement {
  return (
    <FmHeading level={2} id={id} tabIndex={-1}>
      {title}
    </FmHeading>
  );
}
